import { type RoomRateEgsPlan, isApplicable, isWaitingOnFxRates, isWaitingOnGate } from './buildEgsApplyPlan';

export type EgsRowStatus = 'blocked' | 'needsDecision' | 'noChange' | 'waitingOnRates' | 'willChange';

// A room rate waiting on a gate or on the rates also has a blocker set, so both are checked ahead of it.
export function getEgsRowStatus(plan: RoomRateEgsPlan): EgsRowStatus {
  if (isWaitingOnGate(plan)) {
    return 'needsDecision';
  }

  if (isWaitingOnFxRates(plan)) {
    return 'waitingOnRates';
  }

  if (plan.blocker) {
    return 'blocked';
  }

  return isApplicable(plan) ? 'willChange' : 'noChange';
}

// Anything the CM can act on sorts to the top, so a long list does not have to be searched.
// Beside `willChange` rather than `blocked`: it is a row that writes, once the rates it is waiting for arrive.
const STATUS_SORT_ORDER: Record<EgsRowStatus, number> = {
  needsDecision: 0,
  blocked: 1,
  willChange: 2,
  waitingOnRates: 3,
  noChange: 4,
};

export function compareEgsRowStatus(a: EgsRowStatus, b: EgsRowStatus) {
  return STATUS_SORT_ORDER[a] - STATUS_SORT_ORDER[b];
}

function needsEgsAttention(status: EgsRowStatus) {
  return status === 'needsDecision' || status === 'blocked';
}

// Rows only open themselves while the list is short enough to stay navigable.
const MAX_EXPANDED_ROWS = 3;
const MAX_EXPANDED_ATTENTION_ROWS = 5;

export function isEgsRowDefaultExpanded(status: EgsRowStatus, visibleRowCount: number): boolean {
  const limit = needsEgsAttention(status) ? MAX_EXPANDED_ATTENTION_ROWS : MAX_EXPANDED_ROWS;

  return visibleRowCount <= limit;
}
